import React, { useContext, useState, useEffect, useRef } from 'react'
import moment from 'moment'
import styled from 'styled-components'
import { useIntl, Link } from 'gatsby-plugin-intl'
import useValidateSchema from 'src/hooks/useValidateSchema'
import { compose } from 'recompose'
import { breakpoint } from 'src/theme/grid'
import { scrollToRef } from 'src/utils/scroll'
import HeaderLoggedIn, {
  HeaderGroup
} from 'src/components/header/HeaderLoggedIn'
import { Page } from 'src/components/Wrappers'
import Alert from 'src/components/Alert'
import BreadCrumbs from 'src/components/BreadCrumbs'
import { MENU_ITEMS } from 'src/features/dashboard/duck/consts'
import LocationInput from 'src/components/filters/LocationInput'
import routes from 'src/utils/routes'
import { connectDictionaries } from 'src/ducks/connectors'
import {
  OFFER_TYPES,
  OFFER_TYPES_OPTIONS,
  OFFER_VISIBILITY_TYPES,
  OFFER_VISIBILITY_TYPES_OPTIONS
} from 'src/features/offers/duck/consts'
import Checkbox from 'src/components/atoms/CheckboxInput'
import Select from 'src/components/filters/SelectInput'
import TextInput from 'src/components/atoms/TextInput'
import TextInputMasked from 'src/components/atoms/TextInputMasked'
import DatePicker from 'src/components/atoms/DatePicker'
import {
  BOOLEAN,
  LOCATION_TYPES,
  API_STATES,
  EXCHANGE_API_URL_PREFIXES
} from 'src/ducks/consts'
import { EURO_OPTIONS } from 'src/features/stocks/ducks/consts'
import IconWithTooltip from 'src/components/IconWithTooltip'
import NewForeignLocationsAddedAlert from 'src/features/offers/components/NewForeignLocationsAddedAlert'
import PermissionsContext from 'src/utils/permissions'
import PrivateOfferCreateOptions from 'src/features/offers/components/PrivateOfferCreateOptions'
import { useSelector } from 'react-redux'
import { canAddOffer } from 'src/features/offers/utils/exchangePermissions'
import CreateOfferMenu from './components/CreateOfferMenu'
import {
  Content,
  FormSection,
  FormInnerWrapper,
  FormSectionSpacer,
  FormActionBar,
  InputsRowWrapper,
  InputWrapper,
  PlaceInputWrapper,
  DateInputWrapper,
  TimeInputWrapper,
  DictionaryInputWrapper,
  Padding,
  TemplateNameInputWrapper,
  TemplatesButton,
  ActionGroup,
  HideOnMobile,
  FormActionCheckbox,
  RadioWrapper,
  WarningWrapper,
  AuctionTimeInputWrapper,
  PriceInputWrapper,
  AuctionNotesWrapper
} from './components/atoms'
import CheckboxSection from './components/CheckboxSection'
import Drafts from './components/Drafts'
import { FormNavigationHeader } from './components/typography'
import connect from './duck/connectors'
import {
  validateOfferPostImport,
  validateOfferPostImportSchema
} from './duck/schema'

const OfferFormPostImport = ({
  className,
  offerFormPostImport,
  setOfferFormPostImportValue,
  clearPostImportOfferForm,
  fetchLocations,
  availabilityDictionary,
  destinationDictionary,
  dictionaries,
  getPostImportMyCompanyLastOffers,
  getPostImportOfferTemplates,
  lastOffersPostImport,
  offerTemplatesPostImport,
  fillFormUsingTemplatePostImport,
  removeTemplatePostImport,

  createPostImportOffer,
  createBulkPostImportOffers,
  createAndDuplicatePostImportOffer,
  createAndStartNewPostImportOffer
}) => {
  const intl = useIntl()
  const templateRef = useRef(null)
  const formContentRef = useRef(null)

  const permissions = useContext(PermissionsContext)
  const currentUser = useSelector(state => state.account.currentUser)
  const canCreatePublicOffer = canAddOffer(
    EXCHANGE_API_URL_PREFIXES.POST_IMPORT_CONTAINERS,
    permissions,
    OFFER_VISIBILITY_TYPES.PUBLIC
  )
  const canCreatePrivateOffer = canAddOffer(
    EXCHANGE_API_URL_PREFIXES.POST_IMPORT_CONTAINERS,
    permissions,
    OFFER_VISIBILITY_TYPES.PRIVATE
  )
  const visibilityOptions = OFFER_VISIBILITY_TYPES_OPTIONS.map(option => ({
    ...option,
    text: intl.formatMessage({ id: option.text })
  }))
  const availableVisibilityOptions = visibilityOptions.filter(option =>
    option.value === OFFER_VISIBILITY_TYPES.PUBLIC
      ? canCreatePublicOffer
      : canCreatePrivateOffer
  )
  const hasAvailableVisibilityOptions = availableVisibilityOptions.length > 0
  const shouldShowVisibilitySelector = availableVisibilityOptions.length > 1

  const [templatesOpened, setTemplatesOpened] = useState(false)
  const valid = useValidateSchema(
    offerFormPostImport.toJS(),
    validateOfferPostImportSchema
  )

  useEffect(() => {
    getPostImportMyCompanyLastOffers()
    getPostImportOfferTemplates()
    return clearPostImportOfferForm
  }, [])

  useEffect(() => {
    let defaultVisibility = null

    if (currentUser?.isPrivateOfferDefault && canCreatePrivateOffer) {
      defaultVisibility = OFFER_VISIBILITY_TYPES.PRIVATE
    } else if (canCreatePublicOffer) {
      defaultVisibility = OFFER_VISIBILITY_TYPES.PUBLIC
    } else if (canCreatePrivateOffer) {
      defaultVisibility = OFFER_VISIBILITY_TYPES.PRIVATE
    }

    setOfferFormPostImportValue('visibility', defaultVisibility)
  }, [
    canCreatePrivateOffer,
    canCreatePublicOffer,
    currentUser?.isPrivateOfferDefault,
    setOfferFormPostImportValue
  ])

  useEffect(() => {
    if (offerFormPostImport.visibility !== OFFER_VISIBILITY_TYPES.PRIVATE) {
      setOfferFormPostImportValue('rooms', null)
      setOfferFormPostImportValue('goPublicAt', null)
    }
  }, [offerFormPostImport.visibility, setOfferFormPostImportValue])

  const isAuction = offerFormPostImport.type === OFFER_TYPES.AUCTION

  return (
    <Page
      className={className}
      contentPadding={0}
      topBar={
        <HeaderLoggedIn>
          <HeaderGroup>
            <FormNavigationHeader>
              {intl.formatMessage({ id: 'forms.postImportFormTitle' })}
            </FormNavigationHeader>
          </HeaderGroup>
        </HeaderLoggedIn>
      }
    >
      <Content>
        <FormSection>
          <FormInnerWrapper ref={formContentRef}>
            <Padding>
              <BreadCrumbs
                links={[
                  {
                    name: intl.formatMessage({ id: 'forms.dashboard' }),
                    url: `${routes.APP_DASHBOARD}?tab=${MENU_ITEMS.DASHBOARD}`
                  },
                  {
                    name: intl.formatMessage({
                      id: 'forms.exchangePostImportList'
                    }),
                    url: `${routes.APP_DASHBOARD}?tab=${MENU_ITEMS.STOCKS_POST_IMPORT}`
                  },
                  {
                    name: intl.formatMessage({ id: 'forms.addOffer' }),
                    url: null
                  }
                ]}
              />
              <TemplatesButton onClick={() => setTemplatesOpened(true)}>
                {intl.formatMessage({ id: 'forms.copyOrUseTemplate' })}
              </TemplatesButton>
              <NewForeignLocationsAddedAlert />
              <FormSectionSpacer>
                <RadioWrapper>
                  <div>{intl.formatMessage({ id: 'forms.offerType' })}</div>
                  {OFFER_TYPES_OPTIONS.map(option => ({
                    ...option,
                    text: intl.formatMessage({ id: option.text })
                  })).map(opt => (
                    <Checkbox
                      key={opt.value}
                      name='type'
                      value={offerFormPostImport.type}
                      onChange={setOfferFormPostImportValue}
                      type='radio'
                      option={opt}
                    />
                  ))}
                </RadioWrapper>
              </FormSectionSpacer>
              {isAuction && (
                <>
                  <WarningWrapper>
                    <Alert
                      message={intl.formatMessage({
                        id: 'forms.auctionWarning'
                      })}
                      type='info'
                    />
                  </WarningWrapper>
                  <InputsRowWrapper>
                    <DateInputWrapper>
                      <DatePicker
                        label={intl.formatMessage({
                          id: 'forms.auctionEndDate'
                        })}
                        disablePast
                        required
                        name='auctionDeadlineDate'
                        placeholder='dd.mm.rrrr'
                        value={offerFormPostImport.auctionDeadlineDate}
                        onChange={setOfferFormPostImportValue}
                        maxDate={moment().add(6, 'days')}
                      />
                    </DateInputWrapper>
                    <AuctionTimeInputWrapper>
                      <TextInputMasked
                        mask='99:99'
                        label={intl.formatMessage({
                          id: 'forms.auctionEndTime'
                        })}
                        required
                        id='auctionDeadlineTime'
                        name='auctionDeadlineTime'
                        placeholder='gg:mm'
                        value={offerFormPostImport.auctionDeadlineTime}
                        onChange={setOfferFormPostImportValue}
                        validate={validateOfferPostImport}
                        type='tel'
                        calendarTime
                      />
                    </AuctionTimeInputWrapper>
                    <PriceInputWrapper>
                      <TextInput
                        label={intl.formatMessage({
                          id: 'forms.auctionBasePrice'
                        })}
                        id='price'
                        name='price'
                        required
                        placeholder=''
                        value={offerFormPostImport.price}
                        onChange={setOfferFormPostImportValue}
                        validate={validateOfferPostImport}
                        type='currency'
                        onCurrencyChange={setOfferFormPostImportValue}
                        currencyValue={offerFormPostImport.currency}
                      />
                    </PriceInputWrapper>
                    <Checkbox
                      name='isPriceVisible'
                      value={offerFormPostImport.isPriceVisible}
                      option={{
                        value: BOOLEAN.YES,
                        text: intl.formatMessage({
                          id: 'forms.isPriceVisible'
                        })
                      }}
                      onChange={name => {
                        setOfferFormPostImportValue(
                          name,
                          !offerFormPostImport.isPriceVisible
                        )
                      }}
                    />
                    <AuctionNotesWrapper>
                      <TextInput
                        label={intl.formatMessage({
                          id: 'forms.auctionNotes'
                        })}
                        id='notes'
                        name='notes'
                        multiline
                        rows={3}
                        placeholder={intl.formatMessage({
                          id: 'forms.auctionNotesPlaceholder'
                        })}
                        value={offerFormPostImport.notes}
                        onChange={setOfferFormPostImportValue}
                        validate={validateOfferPostImport}
                      />
                    </AuctionNotesWrapper>
                  </InputsRowWrapper>
                </>
              )}
              {shouldShowVisibilitySelector && (
                <>
                  <FormSectionSpacer>
                    <RadioWrapper>
                      <div>
                        {intl.formatMessage({ id: 'offersFilters.visibility' })}
                      </div>
                      {availableVisibilityOptions.map(opt => (
                        <React.Fragment key={opt.value}>
                          <Checkbox
                            name='visibility'
                            value={offerFormPostImport.visibility}
                            onChange={setOfferFormPostImportValue}
                            type='radio'
                            option={opt}
                          />
                          {opt.value === OFFER_VISIBILITY_TYPES.PRIVATE && (
                            <IconWithTooltip
                              title={intl.formatMessage({
                                id: 'forms.privateOfferCreateOptionsInfoMessage'
                              })}
                            />
                          )}
                        </React.Fragment>
                      ))}
                    </RadioWrapper>
                  </FormSectionSpacer>
                </>
              )}
              {!hasAvailableVisibilityOptions && (
                <Alert
                  message={intl.formatMessage({
                    id: 'forms.offerVisibilityNoPermissions'
                  })}
                  type='warning'
                />
              )}
              {/* {JSON.stringify(offerFormExport)} */}
              {offerFormPostImport.visibility ===
                OFFER_VISIBILITY_TYPES.PRIVATE && (
                <PrivateOfferCreateOptions
                  exchangeType={
                    EXCHANGE_API_URL_PREFIXES.POST_IMPORT_CONTAINERS
                  }
                  onGoPublicDateTimeChange={setOfferFormPostImportValue}
                  rooms={offerFormPostImport.rooms}
                  canCreatePublicOffer={canCreatePublicOffer}
                />
              )}

              <FormSectionSpacer>
                {intl.formatMessage({ id: 'forms.tracking' })}
                <IconWithTooltip
                  openTime={3000}
                  title={intl.formatMessage({
                    id: 'forms.trackingHelperCarrier'
                  })}
                />
              </FormSectionSpacer>
              <Checkbox
                name='doesAllowTracking'
                value={offerFormPostImport.doesAllowTracking}
                option={{
                  value: BOOLEAN.YES,
                  text: intl.formatMessage({
                    id: 'forms.doesRequireTrackingCarrier'
                  })
                }}
                onChange={name => {
                  setOfferFormPostImportValue(
                    name,
                    !offerFormPostImport.doesAllowTracking
                  )
                }}
              />
              <FormSectionSpacer>
                {intl.formatMessage({ id: 'forms.availabilitySectionName' })}
              </FormSectionSpacer>
              <InputsRowWrapper>
                <PlaceInputWrapper>
                  <LocationInput
                    required
                    id='availabilityLocationId'
                    label={intl.formatMessage({
                      id: 'forms.availabilityPlaceInputLabel'
                    })}
                    locationName='availabilityLocationId'
                    locationValue={offerFormPostImport.availabilityLocationId}
                    locationPlaceholder={intl.formatMessage({
                      id: 'forms.availabilityPlaceInputLabel'
                    })}
                    onChange={(name, value) =>
                      setOfferFormPostImportValue(name, value)
                    }
                    placeholder={intl.formatMessage({
                      id: 'forms.locationPlaceholder'
                    })}
                    locationTypes={LOCATION_TYPES.CITY}
                    dictionary={availabilityDictionary}
                    isLoading={
                      availabilityDictionary.state === API_STATES.IN_PROGRESS
                    }
                    fetchLocations={fetchLocations}
                    searchHintText={intl.formatMessage({
                      id: 'offersFilters.searchHintText'
                    })}
                  />
                </PlaceInputWrapper>
                <DateInputWrapper>
                  <DatePicker
                    label={intl.formatMessage({
                      id: 'forms.availabilityDateInputLabel'
                    })}
                    disablePast
                    required
                    name='availabilityDate'
                    placeholder='dd.mm.rrrr'
                    value={offerFormPostImport.availabilityDate}
                    onChange={setOfferFormPostImportValue}
                  />
                </DateInputWrapper>
                <TimeInputWrapper>
                  <TextInputMasked
                    mask='99:99'
                    label={intl.formatMessage({
                      id: 'forms.availabilityTimeInputLabel'
                    })}
                    id='availabilityTime'
                    name='availabilityTime'
                    placeholder='gg:mm'
                    value={offerFormPostImport.availabilityTime}
                    onChange={setOfferFormPostImportValue}
                    validate={validateOfferPostImport}
                    type='tel'
                    calendarTime
                  />
                </TimeInputWrapper>
              </InputsRowWrapper>
              <FormSectionSpacer>
                {intl.formatMessage({ id: 'forms.destinationSectionName' })}
              </FormSectionSpacer>
              <InputsRowWrapper>
                <PlaceInputWrapper>
                  <LocationInput
                    id='destinationLocationId'
                    label={intl.formatMessage({
                      id: 'forms.destinationPlaceInputLabel'
                    })}
                    locationName='destinationLocationId'
                    locationValue={offerFormPostImport.destinationLocationId}
                    locationPlaceholder={intl.formatMessage({
                      id: 'forms.destinationPlaceInputLabel'
                    })}
                    onChange={(name, value) =>
                      setOfferFormPostImportValue(name, value)
                    }
                    placeholder={intl.formatMessage({
                      id: 'forms.locationPlaceholder'
                    })}
                    locationTypes={LOCATION_TYPES.TERMINAL}
                    dictionary={destinationDictionary}
                    isLoading={
                      destinationDictionary.state === API_STATES.IN_PROGRESS
                    }
                    fetchLocations={fetchLocations}
                    searchHintText={intl.formatMessage({
                      id: 'offersFilters.searchHintText'
                    })}
                  />
                </PlaceInputWrapper>
              </InputsRowWrapper>
              <FormSectionSpacer>
                {intl.formatMessage({
                  id: 'forms.postImportContainerSectionName'
                })}
              </FormSectionSpacer>
              <InputsRowWrapper>
                <DictionaryInputWrapper>
                  <Select
                    required
                    id='containerId'
                    name='containerId'
                    label={intl.formatMessage({ id: 'forms.container' })}
                    value={offerFormPostImport.containerId}
                    options={dictionaries.containers}
                    onChange={setOfferFormPostImportValue}
                    placeholder={intl.formatMessage({
                      id: 'forms.searchPlaceholder'
                    })}
                  />
                </DictionaryInputWrapper>
                <DictionaryInputWrapper>
                  <Select
                    required
                    id='shipownerId'
                    name='shipownerId'
                    label={intl.formatMessage({ id: 'forms.shipowner' })}
                    value={offerFormPostImport.shipownerId}
                    options={dictionaries.shipowners}
                    onChange={setOfferFormPostImportValue}
                    placeholder={intl.formatMessage({
                      id: 'forms.searchPlaceholder'
                    })}
                  />
                </DictionaryInputWrapper>
              </InputsRowWrapper>
              <InputsRowWrapper>
                <DictionaryInputWrapper>
                  <Select
                    id='adrClassId'
                    name='adrClassId'
                    label={intl.formatMessage({ id: 'forms.adrClass' })}
                    value={offerFormPostImport.adrClassId}
                    options={dictionaries.adrClasses}
                    onChange={setOfferFormPostImportValue}
                    placeholder={intl.formatMessage({
                      id: 'forms.searchPlaceholder'
                    })}
                    width='380px'
                  />
                </DictionaryInputWrapper>
                <DictionaryInputWrapper>
                  <Select
                    id='europeanEmissionStandard'
                    name='europeanEmissionStandard'
                    label={intl.formatMessage({
                      id: 'forms.europeanEmissionStandard'
                    })}
                    value={offerFormPostImport.europeanEmissionStandard}
                    options={EURO_OPTIONS.map(option => ({
                      ...option,
                      label: intl.formatMessage({ id: option.label })
                    }))}
                    onChange={setOfferFormPostImportValue}
                    placeholder={intl.formatMessage({
                      id: 'forms.searchPlaceholder'
                    })}
                    width='230px'
                  />
                </DictionaryInputWrapper>
              </InputsRowWrapper>
              {!isAuction && (
                <>
                  <FormSectionSpacer>
                    {intl.formatMessage({ id: 'forms.additionalInformation' })}
                  </FormSectionSpacer>
                  <InputsRowWrapper>
                    <InputWrapper width={10}>
                      <TextInput
                        label={intl.formatMessage({
                          id: 'forms.additionalInformation'
                        })}
                        id='notes'
                        name='notes'
                        multiline
                        rows={3}
                        placeholder={intl.formatMessage({
                          id: 'forms.additionalInformationPlaceholder'
                        })}
                        value={offerFormPostImport.notes}
                        onChange={setOfferFormPostImportValue}
                        validate={validateOfferPostImport}
                      />
                    </InputWrapper>
                  </InputsRowWrapper>
                </>
              )}
              <FormSectionSpacer />
            </Padding>
            <CheckboxSectionStyled
              ref={templateRef}
              opened={offerFormPostImport.templateNameOpened}
              setOpened={opened =>
                setOfferFormPostImportValue('templateNameOpened', opened)
              }
              label={intl.formatMessage({
                id: 'forms.templateName'
              })}
              handleClose={() =>
                setOfferFormPostImportValue('templateName', null)
              }
            >
              <TemplateNameInputWrapper>
                <TextInput
                  required
                  label={intl.formatMessage({
                    id: 'forms.templateNameLabel'
                  })}
                  id='templateName'
                  name='templateName'
                  placeholder={intl.formatMessage({
                    id: 'forms.templateNamePlaceholder'
                  })}
                  value={offerFormPostImport.templateName}
                  onChange={setOfferFormPostImportValue}
                  validate={validateOfferPostImport}
                />
              </TemplateNameInputWrapper>
            </CheckboxSectionStyled>
          </FormInnerWrapper>
          <OfferFormActionBar>
            <Link
              to={`${routes.APP_DASHBOARD}?tab=${MENU_ITEMS.STOCKS_POST_IMPORT}`}
            >
              {intl.formatMessage({ id: 'forms.cancel' })}
            </Link>
            <ActionGroup>
              <HideOnMobile>
                <FormActionCheckbox
                  name='templateNameOpened'
                  value={offerFormPostImport.templateNameOpened}
                  option={{
                    value: BOOLEAN.YES,
                    text: intl.formatMessage({
                      id: 'forms.templateName'
                    })
                  }}
                  onChange={name => {
                    setOfferFormPostImportValue(
                      name,
                      !offerFormPostImport.templateNameOpened
                    )
                    scrollToRef(templateRef, formContentRef)
                  }}
                />
              </HideOnMobile>
              <CreateOfferMenu
                isLoading={offerFormPostImport.state === API_STATES.IN_PROGRESS}
                active={
                  hasAvailableVisibilityOptions &&
                  valid &&
                  offerFormPostImport.state !== API_STATES.IN_PROGRESS
                }
                createOffer={createPostImportOffer}
                createBulkOffers={createBulkPostImportOffers}
                createAndDuplicateOffer={createAndDuplicatePostImportOffer}
                createAndStartNewOffer={createAndStartNewPostImportOffer}
              >
                {intl.formatMessage({ id: 'forms.addOffer' })}
              </CreateOfferMenu>
            </ActionGroup>
          </OfferFormActionBar>
        </FormSection>
        <Drafts
          fillFormUsingTemplate={fillFormUsingTemplatePostImport}
          opened={templatesOpened}
          onClose={() => setTemplatesOpened(false)}
          lastOffers={lastOffersPostImport}
          templates={offerTemplatesPostImport}
          removeTemplate={removeTemplatePostImport}
        />
      </Content>
    </Page>
  )
}

const ConnectedComponent = compose(
  connect,
  connectDictionaries
)(OfferFormPostImport)

const CheckboxSectionStyled = styled(CheckboxSection)`
  ${breakpoint.m`
    margin-bottom: 330px;
  `}
`

const OfferFormActionBar = styled(FormActionBar)`
  > :first-child {
    margin-right: 1rem;
    flex: 0 0 auto;
  }
`

export default styled(ConnectedComponent)``
